import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Download, Trash2, PackagePlus, X, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCustomFields, CustomFieldsRenderer } from "@/components/CustomFields";

const BATTERY_TYPES = ["Lead Acid", "Li-ion", "AGM", "Gel", "NiCd", "Other"];
const PURCHASE_TYPES = ["New Purchase", "Replacement", "Return", "Sample"];
const PAYMENT_MODES = ["Cash", "Bank Transfer", "UPI", "Credit", "Cheque"];

export default function InwardStock() {
  const { user } = useAuth();
  const [stockType, setStockType] = useState("ready"); // "ready" | "raw"
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm("ready"));
  const { fields: customFields } = useCustomFields("inward");

  function initialForm(st) {
    return {
      stock_type: st || "ready",
      batch_number: "",
      vendor_id: "",
      purchase_date: new Date().toISOString().slice(0, 10),
      purchase_type: "New Purchase",
      brand: "",
      model: "",
      battery_type: "Lead Acid",
      voltage: "",
      capacity_ah: "",
      warranty_months: 12,
      serial_number: "",
      product: "",
      power: "",
      weight: "",
      bms_name: "",
      bms_type: "Smart",
      quantity: 1,
      unit_cost: 0,
      payment_mode: "Cash",
      advance_payment: 0,
      balance_paid: 0,
      balance_pay_date: "",
      balance_payment_mode: "",
      invoice_number: "",
      notes: "",
      low_stock_threshold: 5,
      custom_data: {},
      location_id: "",
    };
  }

  const load = async () => {
    const [i, v, l] = await Promise.all([
      api.get(`/inward?stock_type=${stockType}`),
      api.get("/vendors"),
      api.get("/locations"),
    ]);
    setItems(i.data);
    setVendors(v.data);
    setLocations(l.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stockType]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        stock_type: form.stock_type || stockType,
        quantity: parseInt(form.quantity),
        unit_cost: parseFloat(form.unit_cost),
        warranty_months: parseInt(form.warranty_months || 0),
        low_stock_threshold: parseInt(form.low_stock_threshold || 0),
        advance_payment: parseFloat(form.advance_payment || 0),
        balance_paid: parseFloat(form.balance_paid || 0),
      };
      if (editingId) {
        await api.patch(`/inward/${editingId}`, payload);
        toast.success("Inward record updated");
      } else {
        await api.post("/inward", payload);
        toast.success("Inward stock recorded");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(initialForm(stockType));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      stock_type: it.stock_type || "ready",
      batch_number: it.batch_number,
      vendor_id: it.vendor_id,
      purchase_date: it.purchase_date,
      purchase_type: it.purchase_type,
      brand: it.brand,
      model: it.model,
      battery_type: it.battery_type,
      voltage: it.voltage,
      capacity_ah: it.capacity_ah,
      warranty_months: it.warranty_months || 0,
      serial_number: it.serial_number || "",
      product: it.product || "",
      power: it.power || "",
      weight: it.weight || "",
      bms_name: it.bms_name || "",
      bms_type: it.bms_type || "Smart",
      quantity: it.quantity,
      unit_cost: it.unit_cost,
      payment_mode: it.payment_mode,
      advance_payment: it.advance_payment || 0,
      balance_paid: it.balance_paid || 0,
      balance_pay_date: it.balance_pay_date || "",
      balance_payment_mode: it.balance_payment_mode || "",
      invoice_number: it.invoice_number || "",
      notes: it.notes || "",
      low_stock_threshold: it.low_stock_threshold || 5,
      custom_data: it.custom_data || {},
      location_id: it.location_id || "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(initialForm(stockType));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inward record?")) return;
    try {
      await api.delete(`/inward/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const totalValue = items.reduce((s, i) => s + (i.total_cost || 0), 0);
  const label = stockType === "raw" ? "Raw Material" : "Ready Stock";

  return (
    <div className="p-8 space-y-6" data-testid="inward-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#16A34A] mr-2 align-middle" />Purchases · {label}</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Inward Stock</h1>
          <div className="font-mono text-xs text-zinc-500 mt-2">
            {items.length} records · ₹{totalValue.toLocaleString()} total value
          </div>
        </div>
        <div className="flex gap-3">
          <a href={`${API}/export/inward?stock_type=${stockType}`} className="btn-ghost flex items-center gap-2" data-testid="export-inward-btn">
            <Download className="w-4 h-4" /> Export CSV
          </a>
          <button onClick={() => { setForm(initialForm(stockType)); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="new-inward-btn">
            <Plus className="w-4 h-4" /> New Inward
          </button>
        </div>
      </div>

      <StockTypeTabs value={stockType} onChange={setStockType} testidPrefix="inward" />

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="data-table" data-testid="inward-table">
          <thead>
            <tr>
              <th>Batch #</th>
              <th>Date</th>
              <th>Vendor</th>
              <th>Type</th>
              <th>Brand / Model</th>
              <th>Battery</th>
              <th>Serial</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Unit ₹</th>
              <th className="text-right">Total ₹</th>
              <th>Payment</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="12" className="text-center py-12 text-zinc-500 font-sans">
                <PackagePlus className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No inward records yet. Click <span className="text-[#16A34A] font-semibold">New Inward</span> to start.
              </td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id}>
                <td className="text-[#16A34A] font-semibold">{it.batch_number}</td>
                <td>{it.purchase_date}</td>
                <td className="font-sans">{it.vendor_name}</td>
                <td className="font-sans">{it.purchase_type}</td>
                <td className="font-sans">{it.brand} / {it.model}</td>
                <td>{it.battery_type} · {it.voltage}V · {it.capacity_ah}Ah</td>
                <td>{it.serial_number || "—"}</td>
                <td className="text-right">{it.quantity}</td>
                <td className="text-right">{it.unit_cost.toLocaleString()}</td>
                <td className="text-right text-[#16A34A] font-semibold">{it.total_cost.toLocaleString()}</td>
                <td className="font-sans">{it.payment_mode}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => startEdit(it)} className="text-zinc-400 hover:text-[#16A34A] transition-colors" data-testid={`edit-inward-${it.id}`} title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {user?.role === "admin" && (
                      <button onClick={() => remove(it.id)} className="text-zinc-400 hover:text-[#DC2626] transition-colors" data-testid={`del-inward-${it.id}`} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <FormModal onClose={closeForm}>
          <form onSubmit={submit} className="space-y-5" data-testid="inward-form">
            <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
              <h2 className="font-display text-2xl uppercase text-zinc-900">{editingId ? "Edit Inward Entry" : "New Inward Entry"}</h2>
              <button type="button" onClick={closeForm} className="text-zinc-500 hover:text-zinc-900">
                <X className="w-5 h-5" />
              </button>
            </div>

            <SectionTitle>Stock Type</SectionTitle>
            <div className="flex gap-2" data-testid="in-stock-type">
              {[{ k: "ready", label: "Ready Stock" }, { k: "raw", label: "Raw Material" }].map((o) => (
                <button
                  type="button"
                  key={o.k}
                  onClick={() => setForm({ ...form, stock_type: o.k })}
                  data-testid={`in-stock-type-${o.k}`}
                  className={`px-4 py-2 border text-xs uppercase font-bold tracking-widest transition-colors ${
                    form.stock_type === o.k ? "bg-[#16A34A] border-[#16A34A] text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-[#16A34A] hover:text-[#16A34A]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            <SectionTitle>Batch & Vendor</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Batch Number*"><input required data-testid="in-batch" value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value.toUpperCase() })} className="input-field font-mono" placeholder="BAT-2026-001" /></Field>
              <Field label="Vendor*">
                <select required data-testid="in-vendor" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} className="input-field">
                  <option value="">Select vendor…</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </Field>
              <Field label="Purchase Date*"><input required type="date" data-testid="in-date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="input-field" /></Field>
              <Field label="Purchase Type">
                <select value={form.purchase_type} onChange={(e) => setForm({ ...form, purchase_type: e.target.value })} className="input-field" data-testid="in-ptype">
                  {PURCHASE_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Invoice #"><input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="input-field" data-testid="in-invoice" /></Field>
              <Field label="Location">
                <select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className="input-field" data-testid="in-location">
                  <option value="">— Unassigned —</option>
                  {locations.filter((l) => l.is_active !== false).map((l) => <option key={l.id} value={l.id}>{l.code} · {l.name}</option>)}
                </select>
              </Field>
            </div>

            <SectionTitle>Battery Specifications</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Brand*"><input required data-testid="in-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="input-field" placeholder="Exide" /></Field>
              <Field label="Model*"><input required data-testid="in-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="input-field" placeholder="FML0-FMLO150" /></Field>
              <Field label="Battery Type">
                <select value={form.battery_type} onChange={(e) => setForm({ ...form, battery_type: e.target.value })} className="input-field" data-testid="in-btype">
                  {BATTERY_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Voltage (V)*"><input required data-testid="in-voltage" value={form.voltage} onChange={(e) => setForm({ ...form, voltage: e.target.value })} className="input-field font-mono" placeholder="12" /></Field>
              <Field label="Capacity (Ah)*"><input required data-testid="in-capacity" value={form.capacity_ah} onChange={(e) => setForm({ ...form, capacity_ah: e.target.value })} className="input-field font-mono" placeholder="150" /></Field>
              <Field label="Warranty (Months)"><input type="number" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: e.target.value })} className="input-field font-mono" data-testid="in-warranty" /></Field>
              <Field label="Serial Number"><input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="input-field font-mono" data-testid="in-serial" /></Field>
              <Field label="Product"><input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="input-field" data-testid="in-product" placeholder="e.g. ULTAVOLT" /></Field>
              <Field label="Power"><input value={form.power} onChange={(e) => setForm({ ...form, power: e.target.value })} className="input-field" data-testid="in-power" placeholder="e.g. 5.38 kW" /></Field>
              <Field label="Weight"><input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="input-field" data-testid="in-weight" placeholder="e.g. 45 kg" /></Field>
              <Field label="BMS Name"><input value={form.bms_name} onChange={(e) => setForm({ ...form, bms_name: e.target.value })} className="input-field" data-testid="in-bms-name" placeholder="e.g. JBD, Daly" /></Field>
              <Field label="BMS Type">
                <select value={form.bms_type} onChange={(e) => setForm({ ...form, bms_type: e.target.value })} className="input-field" data-testid="in-bms-type">
                  <option>Smart</option>
                  <option>Hard</option>
                </select>
              </Field>
            </div>

            <SectionTitle>Costing & Payment</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Quantity*"><input required type="number" min="1" data-testid="in-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="input-field font-mono" /></Field>
              <Field label="Unit Cost (₹)*"><input required type="number" step="0.01" data-testid="in-cost" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} className="input-field font-mono" /></Field>
              <Field label="Total (auto)">
                <div className="input-field font-mono text-[#16A34A] font-bold">
                  ₹{(parseFloat(form.unit_cost || 0) * parseInt(form.quantity || 0)).toLocaleString()}
                </div>
              </Field>
              <Field label="Payment Mode">
                <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="input-field" data-testid="in-payment">
                  {PAYMENT_MODES.map((m) => <option key={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Low Stock Threshold"><input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="input-field font-mono" data-testid="in-threshold" /></Field>
              <Field label="Notes" full><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field h-20 resize-none" data-testid="in-notes" /></Field>
            </div>

            <SectionTitle>Advance &amp; Balance Payment</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Advance Payment (₹)"><input type="number" step="0.01" value={form.advance_payment} onChange={(e) => setForm({ ...form, advance_payment: e.target.value })} className="input-field font-mono" data-testid="in-advance" /></Field>
              <Field label="Balance Paid (₹)"><input type="number" step="0.01" value={form.balance_paid} onChange={(e) => setForm({ ...form, balance_paid: e.target.value })} className="input-field font-mono" data-testid="in-balance-paid" /></Field>
              <Field label="Balance Due (auto)">
                <div className="input-field font-mono text-[#28B516] font-bold" data-testid="in-balance-due">
                  ₹{Math.max(0, (parseFloat(form.unit_cost || 0) * parseInt(form.quantity || 0)) - parseFloat(form.advance_payment || 0) - parseFloat(form.balance_paid || 0)).toLocaleString()}
                </div>
              </Field>
              <Field label="Balance Pay Date"><input type="date" value={form.balance_pay_date} onChange={(e) => setForm({ ...form, balance_pay_date: e.target.value })} className="input-field" data-testid="in-balance-date" /></Field>
              <Field label="Balance Payment Mode">
                <select value={form.balance_payment_mode} onChange={(e) => setForm({ ...form, balance_payment_mode: e.target.value })} className="input-field" data-testid="in-balance-mode">
                  <option value="">Select…</option>
                  {PAYMENT_MODES.map((m) => <option key={m}>{m}</option>)}
                </select>
              </Field>
            </div>

            {customFields.length > 0 && (
              <>
                <SectionTitle>Custom Fields</SectionTitle>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <CustomFieldsRenderer
                    fields={customFields}
                    values={form.custom_data}
                    onChange={(cd) => setForm({ ...form, custom_data: cd })}
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={closeForm} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="submit-inward">{editingId ? "Update Inward" : "Record Inward"}</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
}

export function FormModal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white border border-zinc-200 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "col-span-full" : ""}`}>
      <div className="overline mb-2">{label}</div>
      {children}
    </label>
  );
}

export function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <span className="w-1.5 h-1.5 bg-[#16A34A]" />
      <span className="overline text-[11px]">{children}</span>
      <span className="flex-1 h-px bg-zinc-200" />
    </div>
  );
}

export function StockTypeTabs({ value, onChange, testidPrefix = "stock" }) {
  const tabs = [
    { k: "ready", label: "Ready Stock" },
    { k: "raw", label: "Raw Material" },
  ];
  return (
    <div className="flex gap-2 border-b border-zinc-200 pb-0" data-testid={`${testidPrefix}-type-tabs`}>
      {tabs.map((t) => (
        <button
          key={t.k}
          onClick={() => onChange(t.k)}
          data-testid={`${testidPrefix}-type-${t.k}`}
          className={`px-6 py-3 -mb-px text-xs uppercase font-bold tracking-widest border-b-2 transition-colors ${
            value === t.k
              ? "border-[#16A34A] text-[#16A34A]"
              : "border-transparent text-zinc-500 hover:text-zinc-900"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
